/**
 * ToolTimelineRow — one row of the AgentTimeline (ADR-095 Phase 1).
 *
 * Extracted from the absorbed ToolGroup.tsx: preserves the expandable
 * args table + result preview UX exactly, but renders as a single row
 * inside the unified vertical timeline rather than as a collapsible
 * accordion of multiple traces.
 *
 * The parent AgentTimeline renders the status dot + connector line in
 * the gutter to the left; this component owns only the row's CONTENT
 * column (label / result preview / duration / cost / expand-toggle /
 * args table).
 */

import { useState } from "react";

import { labelFor } from "@/lib/toolLabels";
import type { ToolTrace } from "@/types";

function formatDuration(ms: number): string {
  if (ms === 0) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms / 1000)} s`;
}

function formatCost(usd: number): string {
  if (!usd || usd < 0.0001) return "";
  if (usd < 0.01) return `$${(usd * 1000).toFixed(2)}m`;
  return `$${usd.toFixed(4)}`;
}

function friendlyResultSummary(tool: string, raw: string): string {
  if (!raw) return raw;
  const prefix = `${tool}:`;
  const stripped = raw.startsWith(prefix) ? raw.slice(prefix.length).trim() : raw;
  return stripped.replace(/(\d+)\s+candidates\b/i, "$1 sources");
}

function formatArgValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") {
    return v.length > 200 ? `${v.slice(0, 197)}…` : v;
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 200 ? `${s.slice(0, 197)}…` : s;
  } catch {
    return String(v);
  }
}

function ToolArgsTable({ args }: { args: Record<string, unknown> }) {
  const entries = Object.entries(args);
  if (entries.length === 0) {
    return (
      <div className="rounded-[var(--radius-sm)] bg-[var(--surface-raised)] px-[var(--space-2)] py-[var(--space-1)] type-caption italic text-[var(--text-tertiary)]">
        no args
      </div>
    );
  }
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 rounded-[var(--radius-sm)] bg-[var(--surface-raised)] p-[var(--space-2)] type-caption">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="font-mono font-semibold text-[var(--text-secondary)]">{k}</dt>
          <dd className="break-all font-mono text-[var(--text-primary)]">{formatArgValue(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface ToolTimelineRowProps {
  trace: ToolTrace;
}

export function ToolTimelineRow({ trace }: ToolTimelineRowProps) {
  const [expanded, setExpanded] = useState(false);
  const isInflight = trace.duration_ms === 0;
  const hasArgs = Object.keys(trace.args_redacted ?? {}).length > 0;
  const hasResultBody = !!trace.result_summary && trace.result_summary.length > 120;
  const cost = formatCost(trace.cost_usd);
  const friendlySummary = trace.result_summary ? friendlyResultSummary(trace.tool, trace.result_summary) : "";

  return (
    <div className="type-body-sm" data-testid="agent-timeline-tool-row">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={`Toggle details for ${labelFor(trace.tool)}`}
        className="flex w-full items-start gap-2 rounded-[var(--radius-md)] py-0.5 text-left transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)]"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span
              className={`font-medium ${isInflight ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}`}
            >
              {labelFor(trace.tool)}
            </span>
          </div>
          {friendlySummary && (
            <div className="mt-[var(--space-1)] text-[var(--text-secondary)]">
              {friendlySummary.length > 120 ? `${friendlySummary.slice(0, 117)}…` : friendlySummary}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-0.5 type-caption tabular-nums text-[var(--text-tertiary)]">
          <span className="tabular-nums">{formatDuration(trace.duration_ms)}</span>
          {cost && <span className="tabular-nums text-[var(--text-tertiary)]">{cost}</span>}
        </div>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
          className={`mt-1 flex-shrink-0 text-[var(--text-tertiary)] transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)] ${expanded ? "rotate-90" : ""}`}
        >
          <path d="M3 2l3 3-3 3" />
        </svg>
      </button>
      {expanded && (
        <div className="mt-[var(--space-1)] ml-0.5 space-y-[var(--space-2)] rounded-[var(--radius-sm)] border-l border-[var(--border-default)] bg-[var(--surface-sunken)] px-[var(--space-3)] py-[var(--space-2)]">
          <div className="font-mono type-overline text-[var(--text-tertiary)]">{trace.tool}</div>
          {hasArgs && (
            <div>
              <div className="mb-[var(--space-1)] type-overline text-[var(--text-tertiary)]">Arguments</div>
              <ToolArgsTable args={trace.args_redacted} />
            </div>
          )}
          {hasResultBody && trace.result_summary && (
            <div>
              <div className="mb-[var(--space-1)] type-overline text-[var(--text-tertiary)]">Result</div>
              <div className="whitespace-pre-wrap rounded-[var(--radius-sm)] bg-[var(--surface-raised)] p-[var(--space-2)] type-caption text-[var(--text-primary)]">
                {trace.result_summary}
              </div>
            </div>
          )}
          {!hasArgs && !hasResultBody && (
            <div className="type-caption italic text-[var(--text-tertiary)]">no further details</div>
          )}
        </div>
      )}
    </div>
  );
}
